import { useCallback, useMemo, useRef } from "react";
import { clientToSvgUserSpace } from "../../utils/svgViewBoxPointer";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";
import { ArtbrushReferenceLayer } from "./ArtbrushReferenceLayer";
import { ArtbrushStampLayer } from "./ArtbrushStampLayer";
import { ArtbrushStrokeLayer } from "./ArtbrushStrokeLayer";
import {
    useArtbrushSvgCanvas,
    type PenPointerPhase,
    type PenPointerSnapshot,
    type PointerSample,
} from "./ArtbrushSvgCanvasContext";
import {
    ARTBRUSH_PAPER_FILL,
    halfWidthFromPressure,
} from "../../utils/artbrushStroke";
import { useUndoRedoKeyboard } from "../../hooks/useUndoRedoKeyboard";
import "./ArtbrushSvgCanvas.scss";

function snapshotFromEvent(
    svg: SVGSVGElement,
    viewBoxAttr: string,
    event: React.PointerEvent<SVGSVGElement>,
    phase: PenPointerPhase,
    coalescedCount = 0,
    sample?: PointerEvent,
): PenPointerSnapshot {
    const e = sample ?? event.nativeEvent;
    const pt = clientToSvgUserSpace(svg, e.clientX, e.clientY, viewBoxAttr);
    return {
        phase,
        pressed: (e.buttons & 1) !== 0,
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        button: e.button,
        buttons: e.buttons,
        isPrimary: e.isPrimary,
        clientX: e.clientX,
        clientY: e.clientY,
        svgX: pt?.x ?? null,
        svgY: pt?.y ?? null,
        pressure: e.pressure,
        tiltX: e.tiltX,
        tiltY: e.tiltY,
        twist: e.twist,
        tangentialPressure: e.tangentialPressure,
        width: e.width,
        height: e.height,
        coalescedCount,
        timestamp: e.timeStamp,
    };
}

function collectPointerSamples(
    svg: SVGSVGElement,
    viewBoxAttr: string,
    event: React.PointerEvent<SVGSVGElement>,
    includeCoalesced = true,
): PointerSample[] {
    const raw = includeCoalesced
        ? (event.nativeEvent.getCoalescedEvents?.() ?? [])
        : [];
    const events = raw.length > 0 ? raw : [event.nativeEvent];
    const out: PointerSample[] = [];
    for (const e of events) {
        const pt = clientToSvgUserSpace(svg, e.clientX, e.clientY, viewBoxAttr);
        if (!pt) continue;
        out.push({
            x: pt.x,
            y: pt.y,
            pressure: e.pressure,
            pointerType: e.pointerType,
        });
    }
    return out;
}

function smoothPointerSamples(
    samples: PointerSample[],
    smoothingPct: number,
    lastPoint: { x: number; y: number } | null,
): { samples: PointerSample[]; lastPoint: { x: number; y: number } | null } {
    if (samples.length === 0) return { samples, lastPoint };
    const strength = Math.max(0, Math.min(100, smoothingPct)) / 100;
    if (strength <= 0) {
        const last = samples[samples.length - 1]!;
        return { samples, lastPoint: { x: last.x, y: last.y } };
    }
    const alpha = 1 - strength * 0.9;
    const out: PointerSample[] = [];
    let prev = lastPoint;
    for (const s of samples) {
        if (!prev) {
            out.push(s);
            prev = { x: s.x, y: s.y };
            continue;
        }
        const x = prev.x + (s.x - prev.x) * alpha;
        const y = prev.y + (s.y - prev.y) * alpha;
        out.push({ ...s, x, y });
        prev = { x, y };
    }
    return { samples: out, lastPoint: prev };
}

export function ArtbrushSvgCanvasViewer() {
    const {
        tool,
        viewBoxAttr,
        canvasWidth,
        canvasHeight,
        brushSize,
        eraserSize,
        minPressureFactor,
        showPenDebug,
        penSnapshot,
        setPenSnapshot,
        referenceImage,
        showReferenceImage,
        referenceOpacity,
        strokes,
        activeStroke,
        stampStrokes,
        activeStampStroke,
        parsedStampObjects,
        stampObjectSize,
        stampRenderOptions,
        strokeSmoothing,
        beginStroke,
        appendStrokeSamples,
        finishStroke,
        cancelActiveStroke,
        beginStampStroke,
        appendStampStrokeSamples,
        finishStampStroke,
        cancelActiveStampStroke,
        beginEraseStroke,
        appendEraseStrokeSamples,
        finishEraseStroke,
        undo,
        redo,
    } = useArtbrushSvgCanvas();
    const capturePointerIdRef = useRef<number | null>(null);
    const smoothedPointerRef = useRef<{ x: number; y: number } | null>(null);

    useUndoRedoKeyboard({ undo, redo });

    const base = useMemo(() => {
        const [x, y, w, h] = viewBoxAttr.split(" ").map(Number);
        return { x, y, w, h };
    }, [viewBoxAttr]);

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    const pushSnapshot = useCallback(
        (
            event: React.PointerEvent<SVGSVGElement>,
            phase: PenPointerPhase,
            coalescedCount = 0,
            sample?: PointerEvent,
        ) => {
            setPenSnapshot(
                snapshotFromEvent(
                    event.currentTarget,
                    viewBoxAttr,
                    event,
                    phase,
                    coalescedCount,
                    sample,
                ),
            );
        },
        [setPenSnapshot, viewBoxAttr],
    );

    const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
        panHandlers.onPointerDown(event);
        if (event.button !== 0) return;
        const svg = event.currentTarget;
        event.currentTarget.setPointerCapture(event.pointerId);
        capturePointerIdRef.current = event.pointerId;
        smoothedPointerRef.current = null;

        const samples = collectPointerSamples(
            svg,
            viewBoxAttr,
            event,
            tool !== "stamp",
        );
        const smoothed = smoothPointerSamples(
            samples,
            strokeSmoothing,
            smoothedPointerRef.current,
        );
        smoothedPointerRef.current = smoothed.lastPoint;
        if (tool === "stamp") beginStampStroke(smoothed.samples);
        else if (tool === "eraser") beginEraseStroke(smoothed.samples);
        else beginStroke(smoothed.samples);

        const coalesced = event.nativeEvent.getCoalescedEvents?.() ?? [];
        const count = coalesced.length > 0 ? coalesced.length : 1;
        const last = coalesced.length > 0 ? coalesced[count - 1] : undefined;
        pushSnapshot(event, "down", count, last);
    };

    const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
        panHandlers.onPointerMove(event);
        const svg = event.currentTarget;
        const coalesced = event.nativeEvent.getCoalescedEvents?.() ?? [];
        const count = coalesced.length > 0 ? coalesced.length : 1;
        const last = coalesced.length > 0 ? coalesced[count - 1] : undefined;

        if (
            capturePointerIdRef.current === event.pointerId &&
            (event.buttons & 1) !== 0
        ) {
            const samples = collectPointerSamples(
                svg,
                viewBoxAttr,
                event,
                tool !== "stamp",
            );
            const smoothed = smoothPointerSamples(
                samples,
                strokeSmoothing,
                smoothedPointerRef.current,
            );
            smoothedPointerRef.current = smoothed.lastPoint;
            if (tool === "stamp") appendStampStrokeSamples(smoothed.samples);
            else if (tool === "eraser") appendEraseStrokeSamples(smoothed.samples);
            else appendStrokeSamples(smoothed.samples);
        }

        pushSnapshot(event, "move", count, last);
    };

    const finishPointer = (
        event: React.PointerEvent<SVGSVGElement>,
        phase: PenPointerPhase,
    ) => {
        if (capturePointerIdRef.current === event.pointerId) {
            if (tool === "stamp") finishStampStroke();
            else if (tool === "eraser") finishEraseStroke();
            else finishStroke();
            capturePointerIdRef.current = null;
            smoothedPointerRef.current = null;
            try {
                event.currentTarget.releasePointerCapture(event.pointerId);
            } catch {
                /* already released */
            }
        } else if (phase === "cancel") {
            if (tool === "stamp") cancelActiveStampStroke();
            else cancelActiveStroke();
            smoothedPointerRef.current = null;
        }
        pushSnapshot(event, phase);
    };

    const handlePointerUp = (event: React.PointerEvent<SVGSVGElement>) => {
        panHandlers.onPointerUp(event);
        finishPointer(event, "up");
    };

    const handlePointerCancel = (event: React.PointerEvent<SVGSVGElement>) => {
        panHandlers.onPointerCancel(event);
        finishPointer(event, "cancel");
    };

    const handlePointerLeave = (event: React.PointerEvent<SVGSVGElement>) => {
        if (capturePointerIdRef.current === event.pointerId) return;
        pushSnapshot(event, "leave");
    };

    const handlePointerEnter = (event: React.PointerEvent<SVGSVGElement>) => {
        pushSnapshot(event, "enter");
    };

    const previewHalf =
        penSnapshot.svgX != null
            ? tool === "eraser"
                ? eraserSize / 2
                : halfWidthFromPressure(
                      penSnapshot.pressure,
                      penSnapshot.pointerType,
                      brushSize,
                      minPressureFactor,
                  )
            : tool === "eraser"
              ? eraserSize / 2
              : brushSize / 2;
    const showToolPreviewCursor =
        (tool === "brush" || tool === "eraser") &&
        penSnapshot.svgX != null &&
        penSnapshot.svgY != null;

    return (
        <div className="panel-viewer artbrush-svg-canvas-viewer">
            <div className="path-tool-preview artbrush-path-tool-preview">
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>

                <div className="svg-scroll-wrapper artbrush-svg-canvas-viewer-scroll">
                    <div className="svg-container svg-container--fill artbrush-svg-canvas-container">
                        <svg
                            ref={svgRef}
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox={viewBox}
                            className="artbrush-svg-canvas__svg"
                            style={{ touchAction: "none" }}
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUp}
                            onPointerCancel={handlePointerCancel}
                            onPointerLeave={handlePointerLeave}
                            onPointerEnter={handlePointerEnter}
                        >
                            <rect
                                x={0}
                                y={0}
                                width={canvasWidth}
                                height={canvasHeight}
                                fill={ARTBRUSH_PAPER_FILL}
                                className="artbrush-svg-canvas__paper"
                            />
                            {showReferenceImage && referenceImage && (
                                <ArtbrushReferenceLayer
                                    reference={referenceImage}
                                    opacity={referenceOpacity}
                                />
                            )}
                            <ArtbrushStampLayer
                                stampStrokes={stampStrokes}
                                activeStampStroke={activeStampStroke}
                                objects={parsedStampObjects}
                                renderOptions={stampRenderOptions}
                            />
                            <ArtbrushStrokeLayer
                                strokes={strokes}
                                activeStroke={activeStroke}
                            />
                            {showToolPreviewCursor && (
                                <g className="artbrush-svg-canvas__debug-layer">
                                    <circle
                                        cx={penSnapshot.svgX!}
                                        cy={penSnapshot.svgY!}
                                        r={previewHalf}
                                        className={`artbrush-svg-canvas__debug-cursor ${penSnapshot.pressed ? "artbrush-svg-canvas__debug-cursor--down" : ""}`}
                                    />
                                </g>
                            )}
                            {showPenDebug &&
                                penSnapshot.svgX != null &&
                                penSnapshot.svgY != null && (
                                    <g className="artbrush-svg-canvas__debug-layer">
                                        <line
                                            x1={penSnapshot.svgX}
                                            y1={0}
                                            x2={penSnapshot.svgX}
                                            y2={canvasHeight}
                                            className="artbrush-svg-canvas__debug-cross"
                                        />
                                        <line
                                            x1={0}
                                            y1={penSnapshot.svgY}
                                            x2={canvasWidth}
                                            y2={penSnapshot.svgY}
                                            className="artbrush-svg-canvas__debug-cross"
                                        />
                                        <circle
                                            cx={penSnapshot.svgX}
                                            cy={penSnapshot.svgY}
                                            r={previewHalf}
                                            className={`artbrush-svg-canvas__debug-cursor ${penSnapshot.pressed ? "artbrush-svg-canvas__debug-cursor--down" : ""}`}
                                        />
                                    </g>
                                )}
                        </svg>
                    </div>
                </div>
                <p className="artbrush-svg-canvas-hint">
                    {tool === "stamp"
                        ? "Stamp brush places the object along your stroke every spacing interval."
                        : tool === "eraser"
                          ? "Eraser removes nearby brush strokes and stamp instances."
                        : "Brush size follows pen pressure."}{" "}
                    Zoom affects view only, not export.
                </p>
            </div>
        </div>
    );
}
